import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Container,
  Spinner,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import { requestJson } from "../../../helpers/api";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import baconLogo from "../../../assets/images/baconappen.png";

const formatearTituloColumna = (clave: string) =>
  clave
    .split("_")
    .map((segmento) => segmento.charAt(0).toUpperCase() + segmento.slice(1))
    .join(" ");

const ensureText = (valor: unknown) => {
  if (typeof valor === "string") {
    return valor;
  }
  if (valor == null) {
    return "";
  }
  return String(valor);
};

const resolverUsuarioActual = () => {
  if (typeof window === "undefined") {
    return "Usuario no identificado";
  }
  const raw =
    sessionStorage.getItem("authUser") ||
    localStorage.getItem("authUser") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("user");
  if (!raw) {
    return "Usuario no identificado";
  }
  try {
    const parsed = JSON.parse(raw);
    const nested =
      parsed?.usuario ||
      parsed?.user ||
      parsed?.datosUsuario ||
      parsed?.data?.usuario ||
      parsed?.data?.user;
    const candidatos = [
      parsed?.name,
      parsed?.fullName,
      parsed?.username,
      parsed?.email,
      nested?.nombre,
      nested?.nombreUsuario,
      nested?.username,
      nested?.email,
    ];
    const texto = candidatos.find((valor) => typeof valor === "string" && valor.trim().length > 0);
    if (texto) {
      return texto;
    }
  } catch {
    // ignorar
  }
  return "Usuario no identificado";
};

const ContabilidadArticulosCuentasPage = () => {
  const [datos, setDatos] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);
  const [filtroServidor, setFiltroServidor] = useState("");
  const [filtroCuentaC, setFiltroCuentaC] = useState("");
  const [filtroCuentaVgr, setFiltroCuentaVgr] = useState("");
  const [filtroCuentaVex, setFiltroCuentaVex] = useState("");
  const [soloSinCuentaC, setSoloSinCuentaC] = useState(false);
  const [soloSinCuentaVgr, setSoloSinCuentaVgr] = useState(false);
  const [soloSinCuentaVex, setSoloSinCuentaVex] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Contabilidad - Artículos y cuentas | Saas";
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const respuesta = await requestJson<any[]>("/api/informes/contabilidad/articulos-cuentas");
      setDatos(Array.isArray(respuesta) ? respuesta : []);
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : "No pudimos obtener la información.";
      setError(mensaje);
      setDatos([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    let activo = true;
    const cargarLogo = async () => {
      try {
        const fuentes = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const fuente of fuentes) {
          if (!fuente) {
            continue;
          }
          try {
            const respuesta = await fetch(fuente);
            if (!respuesta.ok) {
              continue;
            }
            const blob = await respuesta.blob();
            const lector = new FileReader();
            const resultado = await new Promise<string>((resolve, reject) => {
              lector.onloadend = () => {
                if (typeof lector.result === "string") {
                  resolve(lector.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              lector.onerror = () => reject(new Error("No pudimos leer el logo."));
              lector.readAsDataURL(blob);
            });
            if (activo) {
              setLogoDataUrl(resultado);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // ignorado
      }
    };
    void cargarLogo();
    return () => {
      activo = false;
    };
  }, []);

  const nombrePlanPorColumna = useMemo(() => {
    if (datos.length === 0) {
      return {};
    }
    return Object.keys(datos[0]).reduce<Record<string, string>>((acc, clave) => {
      if (clave.startsWith("plan_cuenta") && clave.endsWith("_nombre")) {
        const middle = clave.slice("plan_cuenta".length, -"_nombre".length);
        const base = `idplancuenta${middle}`;
        acc[base] = clave;
      }
      return acc;
    }, {});
  }, [datos]);

  const columnas = useMemo(() => {
    if (datos.length === 0) {
      return [];
    }
    const claves = Object.keys(datos[0]).filter(
      (clave) => !(clave.startsWith("plan_cuenta") && clave.endsWith("_nombre")),
    );
    return claves.map((clave) => ({
      header: formatearTituloColumna(clave),
      accessorKey: clave,
      cell: (cell: any) => {
        const nombreCampo = nombrePlanPorColumna[clave];
        const nombre = nombreCampo ? cell.row?.original?.[nombreCampo] : null;
        if (nombreCampo && nombre) {
          const numero = String(cell.getValue() ?? "");
          return (
            <span className="d-inline-flex flex-column text-nowrap">
              <span className="fw-semibold">{numero || "—"}</span>
              <small className="text-muted text-truncate">{String(nombre)}</small>
            </span>
          );
        }
        return <span className="text-nowrap">{String(cell.getValue() ?? "")}</span>;
      },
    }));
  }, [datos, nombrePlanPorColumna]);
  const clavesColumnas = useMemo(() => {
    if (datos.length === 0) {
      return [];
    }
    return Object.keys(datos[0]).filter(
      (clave) => !(clave.startsWith("plan_cuenta") && clave.endsWith("_nombre")),
    );
  }, [datos]);

  const servidoresDisponibles = useMemo(() => {
    const conjunto = new Set<string>();
    datos.forEach((fila) => {
      const valor = String(fila.server ?? "").trim();
      if (valor) {
        conjunto.add(valor);
      }
    });
    return Array.from(conjunto).sort((a, b) => a.localeCompare(b));
  }, [datos]);

  const registrosFiltrados = useMemo(() => {
    const normalizar = (valor: unknown) => String(valor ?? "").trim().toLowerCase();
    const coincideTexto = (valor: unknown, nombre: unknown, filtro: string) => {
      if (!filtro.trim()) {
        return true;
      }
      const buscar = filtro.trim().toLowerCase();
      return normalizar(valor).includes(buscar) || normalizar(nombre).includes(buscar);
    };
    const sinDato = (valor: unknown, nombre: unknown) => {
      return normalizar(valor) === "" && normalizar(nombre) === "";
    };
    return datos.filter((fila) => {
      if (filtroServidor && normalizar(fila.server) !== filtroServidor.toLowerCase()) {
        return false;
      }
      if (!coincideTexto(fila.idplancuenta_c, fila.plan_cuenta_c_nombre, filtroCuentaC)) {
        return false;
      }
      if (!coincideTexto(fila.idplancuenta_vgr, fila.plan_cuenta_vgr_nombre, filtroCuentaVgr)) {
        return false;
      }
      if (!coincideTexto(fila.idplancuenta_vex, fila.plan_cuenta_vex_nombre, filtroCuentaVex)) {
        return false;
      }
      if (soloSinCuentaC && !sinDato(fila.idplancuenta_c, fila.plan_cuenta_c_nombre)) {
        return false;
      }
      if (soloSinCuentaVgr && !sinDato(fila.idplancuenta_vgr, fila.plan_cuenta_vgr_nombre)) {
        return false;
      }
      if (soloSinCuentaVex && !sinDato(fila.idplancuenta_vex, fila.plan_cuenta_vex_nombre)) {
        return false;
      }
      return true;
    });
  }, [
    datos,
    filtroServidor,
    filtroCuentaC,
    filtroCuentaVgr,
    filtroCuentaVex,
    soloSinCuentaC,
    soloSinCuentaVgr,
    soloSinCuentaVex,
  ]);

  const limpiarFiltros = () => {
    setFiltroServidor("");
    setFiltroCuentaC("");
    setFiltroCuentaVgr("");
    setFiltroCuentaVex("");
    setSoloSinCuentaC(false);
    setSoloSinCuentaVgr(false);
    setSoloSinCuentaVex(false);
  };

  const manejarExportarExcel = useCallback(() => {
    if (registrosFiltrados.length === 0) {
      return;
    }
    setExportError(null);
    setExportandoExcel(true);
    try {
      const datosExportacion = registrosFiltrados.map((fila) => {
        const copia: Record<string, unknown> = {};
        Object.entries(fila).forEach(([clave, valor]) => {
          if (clave.startsWith("plan_cuenta") && clave.endsWith("_nombre")) {
            return;
          }
          let contenido: unknown = valor;
          const nombreCampo = nombrePlanPorColumna[clave];
          if (nombreCampo && fila[nombreCampo]) {
            const baseTexto = valor == null ? "" : String(valor);
            const descripcion = String(fila[nombreCampo] ?? "");
            contenido = descripcion ? `${baseTexto} - ${descripcion}` : baseTexto;
          }
          copia[clave] = contenido;
        });
        return copia;
      });
      const worksheet = XLSX.utils.json_to_sheet(datosExportacion);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "articulos_cuentas");
      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `articulos-cuentas-${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      const mensaje =
        err instanceof Error ? err.message : "No pudimos generar la planilla de Excel. Intentá nuevamente.";
      setExportError(mensaje);
    } finally {
      setExportandoExcel(false);
    }
  }, [registrosFiltrados, nombrePlanPorColumna]);

  const manejarExportarPdf = useCallback(() => {
    if (registrosFiltrados.length === 0 || clavesColumnas.length === 0) {
      return;
    }
    setExportError(null);
    setExportandoPdf(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      let pageWidth = doc.internal.pageSize.getWidth();
      let pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 28;
      const marginY = 28;
      const lineHeight = 12;
      const headerHeight = 110;
      const columnWidth = Math.max((pageWidth - marginX * 2) / clavesColumnas.length, 60);
      const usuarioTexto = ensureText(resolverUsuarioActual());
      const timestamp = new Date();
      const timestampTexto = timestamp.toLocaleString("es-PY", { dateStyle: "short", timeStyle: "medium" });
      const breadcrumbTexto = "Informes / Contabilidad / Artículos y cuentas";
      const tituloTexto = "Artículos vinculados a cuentas contables";
      const descripcionTexto =
        "Listado consolidado desde las bases operativas. Filtrá o exportá la información para validar la configuración contable de cada artículo.";
      let currentY = marginY;
      let pageNumber = 1;

      const renderFooter = () => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const footerText = `Generado por ${usuarioTexto} · ${timestampTexto} · Página ${pageNumber}`;
        doc.text(footerText, marginX, pageHeight - marginY / 2);
      };

      const renderHeader = (isFirstPage: boolean) => {
        if (!isFirstPage) {
          renderFooter();
          doc.addPage();
          pageNumber += 1;
          pageWidth = doc.internal.pageSize.getWidth();
          pageHeight = doc.internal.pageSize.getHeight();
        }
        currentY = marginY;
        if (logoDataUrl) {
          const logoWidth = 120;
          const logoHeight = 26;
          doc.addImage(logoDataUrl, "PNG", marginX, currentY - 4, logoWidth, logoHeight);
          currentY += logoHeight;
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(breadcrumbTexto, marginX, currentY + 6);
        currentY += 16;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.text(tituloTexto, marginX, currentY + 6);
        currentY += 20;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(descripcionTexto, marginX, currentY + 4, {
          maxWidth: pageWidth - marginX * 2 - 150,
        });
        doc.setFont("helvetica", "bold");
        doc.text("Usuario:", pageWidth - marginX, marginY + 14, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(usuarioTexto || "—", pageWidth - marginX, marginY + 26, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Generado:", pageWidth - marginX, marginY + 38, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(timestampTexto || "—", pageWidth - marginX, marginY + 50, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`Página ${pageNumber}`, pageWidth - marginX, marginY + 62, { align: "right" });
        currentY = marginY + headerHeight;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        clavesColumnas.forEach((clave, index) => {
          const headerX = marginX + index * columnWidth;
          const headerText = formatearTituloColumna(clave);
          const lines = doc.splitTextToSize(headerText, columnWidth - 6);
          lines.forEach((linea: string, lineaIdx: number) => {
            doc.text(linea, headerX, currentY + lineaIdx * lineHeight);
          });
        });
        currentY += lineHeight * 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
      };

      const renderRow = (row: Record<string, unknown>) => {
        const values = clavesColumnas.map((clave) => {
          const valor = row[clave];
          if (valor === null || valor === undefined) {
            return "";
          }
          if (typeof valor === "number") {
            return Intl.NumberFormat("es-PY").format(valor);
          }
          const nombreCampo = nombrePlanPorColumna[clave];
          if (nombreCampo && row[nombreCampo]) {
            const descripcion = String(row[nombreCampo] ?? "");
            const base = String(valor);
            return descripcion ? `${base} - ${descripcion}` : base;
          }
          return String(valor);
        });
        const rowLines = values.map((texto) => doc.splitTextToSize(texto || "-", columnWidth - 6));
        const maxLines = Math.max(...rowLines.map((lineas) => (lineas.length > 0 ? lineas.length : 1)));
        const requiredHeight = maxLines * lineHeight + 6;
        if (currentY + requiredHeight > pageHeight - marginY) {
          renderHeader(false);
        }
        rowLines.forEach((lineas, index) => {
          const baseX = marginX + index * columnWidth;
          lineas.forEach((linea: string, lineaIdx: number) => {
            doc.text(linea, baseX, currentY + lineaIdx * lineHeight);
          });
        });
        currentY += requiredHeight;
      };

      renderHeader(true);
      registrosFiltrados.forEach((fila) => renderRow(fila));
      renderFooter();
      const timestampArchivo = timestamp.toISOString().slice(0, 10);
      doc.save(`articulos-cuentas-${timestampArchivo}.pdf`);
    } catch (err) {
      console.error(err);
      const mensaje =
        err instanceof Error ? err.message : "No pudimos generar el PDF. Intentá nuevamente.";
      setExportError(mensaje);
    } finally {
      setExportandoPdf(false);
    }
  }, [registrosFiltrados, clavesColumnas, nombrePlanPorColumna, logoDataUrl]);

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb
          title="Artículos vinculados a cuentas contables"
          pageTitle="Contabilidad"
          description="Listado consolidado desde las bases operativas. Filtrá o exportá la información para validar la configuración contable de cada artículo."
        />
        <Card className="border-0 shadow-sm">
          <CardBody>
            <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3 mb-3">
              <div className="flex-grow-1" />
              <div className="d-flex flex-wrap align-items-center gap-2 w-100">
                <Button color="primary" outline className="ms-lg-auto order-lg-2" onClick={() => setFiltrosVisibles(true)}>
                  <i className="ri-filter-3-line me-1" />
                  Filtros
                </Button>
                <div className="d-flex flex-wrap align-items-center gap-2 order-lg-1">
                  <Button color="primary" onClick={() => void cargarDatos()} disabled={cargando}>
                    {cargando ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Actualizando
                      </>
                    ) : (
                    <>
                      <i className="ri-refresh-line me-1" /> Actualizar
                    </>
                  )}
                </Button>
                  <Button
                    color="success"
                    outline
                    onClick={manejarExportarExcel}
                    disabled={registrosFiltrados.length === 0 || exportandoExcel}
                  >
                    {exportandoExcel ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Generando Excel
                      </>
                    ) : (
                      <>
                        <i className="ri-file-excel-2-line me-1" /> Descargar Excel
                      </>
                    )}
                  </Button>
                  <Button
                    color="danger"
                    outline
                    onClick={manejarExportarPdf}
                    disabled={registrosFiltrados.length === 0 || exportandoPdf}
                  >
                    {exportandoPdf ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Generando PDF
                      </>
                    ) : (
                      <>
                        <i className="ri-file-pdf-line me-1" /> Descargar PDF
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {error ? <Alert color="danger">{error}</Alert> : null}
            {exportError ? (
              <Alert color="warning" toggle={() => setExportError(null)}>
                {exportError}
              </Alert>
            ) : null}
            {cargando && datos.length === 0 ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : datos.length === 0 ? (
              <div className="text-center text-muted py-5">No encontramos registros para mostrar.</div>
            ) : registrosFiltrados.length === 0 ? (
              <div className="text-center text-muted py-5">
                No hay registros que coincidan con los filtros seleccionados.
              </div>
            ) : (
              <TableContainer
                columns={columnas}
                data={registrosFiltrados}
                isGlobalFilter={false}
                customPageSize={25}
                divClass="table-responsive"
                tableClass="table align-middle table-striped"
                theadClass="table-light"
              />
            )}
          </CardBody>
        </Card>
      </Container>
      <ArticulosCuentasFilters
        filtroServidor={filtroServidor}
        setFiltroServidor={setFiltroServidor}
        servidoresDisponibles={servidoresDisponibles}
        filtroCuentaC={filtroCuentaC}
        setFiltroCuentaC={setFiltroCuentaC}
        soloSinCuentaC={soloSinCuentaC}
        setSoloSinCuentaC={setSoloSinCuentaC}
        filtroCuentaVgr={filtroCuentaVgr}
        setFiltroCuentaVgr={setFiltroCuentaVgr}
        soloSinCuentaVgr={soloSinCuentaVgr}
        setSoloSinCuentaVgr={setSoloSinCuentaVgr}
        filtroCuentaVex={filtroCuentaVex}
        setFiltroCuentaVex={setFiltroCuentaVex}
        soloSinCuentaVex={soloSinCuentaVex}
        setSoloSinCuentaVex={setSoloSinCuentaVex}
        limpiarFiltros={limpiarFiltros}
        filtrosVisibles={filtrosVisibles}
        setFiltrosVisibles={setFiltrosVisibles}
      />
    </div>
  );
};

type ArticulosCuentasFiltersProps = {
  filtroServidor: string;
  setFiltroServidor: (valor: string) => void;
  servidoresDisponibles: string[];
  filtroCuentaC: string;
  setFiltroCuentaC: (valor: string) => void;
  soloSinCuentaC: boolean;
  setSoloSinCuentaC: (valor: boolean) => void;
  filtroCuentaVgr: string;
  setFiltroCuentaVgr: (valor: string) => void;
  soloSinCuentaVgr: boolean;
  setSoloSinCuentaVgr: (valor: boolean) => void;
  filtroCuentaVex: string;
  setFiltroCuentaVex: (valor: string) => void;
  soloSinCuentaVex: boolean;
  setSoloSinCuentaVex: (valor: boolean) => void;
  limpiarFiltros: () => void;
  filtrosVisibles: boolean;
  setFiltrosVisibles: (valor: boolean) => void;
};

const ArticulosCuentasFilters: React.FC<ArticulosCuentasFiltersProps> = ({
  filtroServidor,
  setFiltroServidor,
  servidoresDisponibles,
  filtroCuentaC,
  setFiltroCuentaC,
  soloSinCuentaC,
  setSoloSinCuentaC,
  filtroCuentaVgr,
  setFiltroCuentaVgr,
  soloSinCuentaVgr,
  setSoloSinCuentaVgr,
  filtroCuentaVex,
  setFiltroCuentaVex,
  soloSinCuentaVex,
  setSoloSinCuentaVex,
  limpiarFiltros,
  filtrosVisibles,
  setFiltrosVisibles,
}) => {
  return (
    <Offcanvas direction="end" scrollable isOpen={filtrosVisibles} toggle={() => setFiltrosVisibles(false)}>
        <OffcanvasHeader toggle={() => setFiltrosVisibles(false)}>
          Filtros de artículos
        </OffcanvasHeader>
        <OffcanvasBody>
          <FormGroup>
            <Label for="filtro-servidor">Servidor origen</Label>
            <Input
              id="filtro-servidor"
              type="select"
              value={filtroServidor}
              onChange={(event) => setFiltroServidor(event.target.value)}
            >
              <option value="">Todos</option>
              {servidoresDisponibles.map((servidor: string) => (
                <option key={servidor} value={servidor.toLowerCase()}>
                  {servidor}
                </option>
              ))}
            </Input>
          </FormGroup>
          <hr />
          <FormGroup>
            <Label for="filtro-cuenta-c">Cuenta de compras (C)</Label>
            <Input
              id="filtro-cuenta-c"
              value={filtroCuentaC}
              onChange={(event) => setFiltroCuentaC(event.target.value)}
              placeholder="Buscar por número o nombre"
            />
            <div className="form-check mt-2">
              <Input
                id="solo-sin-c"
                type="checkbox"
                className="form-check-input"
                checked={soloSinCuentaC}
                onChange={(event) => setSoloSinCuentaC(event.target.checked)}
              />
              <Label className="form-check-label" htmlFor="solo-sin-c">
                Mostrar sólo artículos sin cuenta C asignada
              </Label>
            </div>
          </FormGroup>
          <FormGroup>
            <Label for="filtro-cuenta-vgr">Cuenta ventas gravadas (VGR)</Label>
            <Input
              id="filtro-cuenta-vgr"
              value={filtroCuentaVgr}
              onChange={(event) => setFiltroCuentaVgr(event.target.value)}
              placeholder="Buscar por número o nombre"
            />
            <div className="form-check mt-2">
              <Input
                id="solo-sin-vgr"
                type="checkbox"
                className="form-check-input"
                checked={soloSinCuentaVgr}
                onChange={(event) => setSoloSinCuentaVgr(event.target.checked)}
              />
              <Label className="form-check-label" htmlFor="solo-sin-vgr">
                Mostrar sólo artículos sin cuenta VGR asignada
              </Label>
            </div>
          </FormGroup>
          <FormGroup>
            <Label for="filtro-cuenta-vex">Cuenta ventas exentas (VEX)</Label>
            <Input
              id="filtro-cuenta-vex"
              value={filtroCuentaVex}
              onChange={(event) => setFiltroCuentaVex(event.target.value)}
              placeholder="Buscar por número o nombre"
            />
            <div className="form-check mt-2">
              <Input
                id="solo-sin-vex"
                type="checkbox"
                className="form-check-input"
                checked={soloSinCuentaVex}
                onChange={(event) => setSoloSinCuentaVex(event.target.checked)}
              />
              <Label className="form-check-label" htmlFor="solo-sin-vex">
                Mostrar sólo artículos sin cuenta VEX asignada
              </Label>
            </div>
          </FormGroup>
          <div className="d-flex gap-2 mt-3">
            <Button color="primary" onClick={() => setFiltrosVisibles(false)}>
              Aplicar filtros
            </Button>
            <Button color="light" type="button" onClick={limpiarFiltros}>
              Limpiar
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
  );
};

export default ContabilidadArticulosCuentasPage;
